import type { DiagnosticFact } from "src/api/client-api/diagnosticFacts/useDiagnosticFacts";
import type { PathMetadata } from "./types";

const CALC_PREFIX = "calc.";

const isScoreIdSegment = (parentSegment: string | undefined, segment: string): boolean =>
    parentSegment === "score" && /^\d+$/.test(segment);

const stripCalcPrefix = (factKey: string): string | null => {
    if (!factKey.startsWith(CALC_PREFIX)) {
        return null;
    }
    return factKey.slice(CALC_PREFIX.length);
};

const labelForScoreId = (scoreId: string, scoreTitleById: ReadonlyMap<string, string>): string | undefined => {
    return scoreTitleById.get(scoreId);
};

const enrichScoreIdNode = (
    path: string,
    scoreTitleById: ReadonlyMap<string, string>,
): Pick<PathMetadata, "displayLabel" | "description"> => {
    const segments = path.split(".");
    const scoreId = segments.at(-1);
    const parent = segments.at(-2);

    if (!scoreId || !isScoreIdSegment(parent, scoreId)) {
        return {};
    }

    const title = labelForScoreId(scoreId, scoreTitleById);

    return {
        displayLabel: title,
        description: title ? scoreId : undefined,
    };
};

export const buildScoreTitleById = (scores: { id: number; title: string }[] | undefined): Map<string, string> => {
    const map = new Map<string, string>();
    for (const score of scores ?? []) {
        map.set(String(score.id), score.title);
    }
    return map;
};

/**
 * Expands flat calc fact keys into every path prefix (like extractPaths) so token
 * autocomplete can drill down score → id → heading → …
 */
export const buildCalcSuggestionPaths = (
    facts: Record<string, DiagnosticFact>,
    scoreTitleById: ReadonlyMap<string, string>,
    catalog?: Record<string, string>,
): PathMetadata[] => {
    // Computed facts keep their own label; the catalog only fills keys without an answer yet.
    const labelByFactKey: Record<string, string> = { ...catalog };
    for (const [factKey, fact] of Object.entries(facts)) {
        labelByFactKey[factKey] = fact.label;
    }

    const pathToLeafLabel = new Map<string, string>();

    for (const [factKey, label] of Object.entries(labelByFactKey)) {
        const relativePath = stripCalcPrefix(factKey);
        if (!relativePath) {
            continue;
        }

        pathToLeafLabel.set(relativePath, label);

        const segments = relativePath.split(".");
        for (let depth = 1; depth <= segments.length; depth += 1) {
            const prefix = segments.slice(0, depth).join(".");
            if (!pathToLeafLabel.has(prefix)) {
                pathToLeafLabel.set(prefix, "");
            }
        }
    }

    const paths = Array.from(pathToLeafLabel.keys()).sort((a, b) => a.localeCompare(b));

    return paths.map((path) => {
        const leafLabel = pathToLeafLabel.get(path);
        const labelFromFact = leafLabel && leafLabel.length > 0 ? leafLabel : undefined;

        const totalSiblingLabel = pathToLeafLabel.get(`${path}.total`);
        const intermediateLabel =
            !labelFromFact && totalSiblingLabel && totalSiblingLabel.length > 0 ? totalSiblingLabel : undefined;

        const description = labelFromFact ?? intermediateLabel;

        return {
            path,
            isArray: false,
            description,
            ...enrichScoreIdNode(path, scoreTitleById),
        };
    });
};
